import _extends from "@babel/runtime/helpers/esm/extends";
import _slicedToArray from "@babel/runtime/helpers/esm/slicedToArray";
import _objectWithoutProperties from "@babel/runtime/helpers/esm/objectWithoutProperties";
var _excluded = ["dateFormat", "defaultSelected", "disabledDates", "minDate", "maxDate", "selected", "onChange"];
import { addDays, addMonths, addYears, format, getDay, getMonth, getYear, isSameDay, isValid, setYear } from 'date-fns';
import PropTypes from 'prop-types';
import React, { forwardRef, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { DATE_PICKER_CHANGE_REASON, DATE_PICKER_DAYS, DATE_PICKER_MONTHS, KEY_CODES } from '../../constants';
import { getArrayFromRange, getFirstAvailableDate, getMonthMatrix, getNextAvailableDay, getParsedDates, getPreviousAvailableDay, getValidYear, isDateInBetween, isDisabledDate } from '../../util/dates';
import { identifier } from '../../util/identifier';
import { useIsomorphicLayoutEffect } from '../../util/useIsomorphicLayoutEffect';
import { DatePickerButton } from '../DatePickerButton/DatePickerButton';
import { DatePickerSelect } from '../DatePickerSelect/DatePickerSelect';
import { Flex } from '../Flex/Flex';
import { Heading } from '../Heading/Heading';
import { IconChevronLeft, IconChevronRight } from '../Icon/index';
import { Text } from '../Text/Text';
import { VisuallyHidden } from '../VisuallyHidden/VisuallyHidden';
import { StyledDatePicker } from './DatePicker.styles';

/**
 * Follows [WAI-ARIA accessible](https://www.w3.org/TR/wai-aria-practices/examples/dialog-modal/datepicker-dialog.html)
 * specs.
 *
 * Date picker uses the [date-fns](https://date-fns.org/) library for parsing, formatting, and
 * general manipulation of dates.
 *
 * @subcomponents [`DatePickerInput`]
 * @since 8.0.0
 */
export var DatePicker = /*#__PURE__*/forwardRef(function (_ref, forwardedRef) {
  var dateFormat = _ref.dateFormat,
    defaultSelected = _ref.defaultSelected,
    disabledDates = _ref.disabledDates,
    minDate = _ref.minDate,
    maxDate = _ref.maxDate,
    selected = _ref.selected,
    onChange = _ref.onChange,
    rest = _objectWithoutProperties(_ref, _excluded);
  /** *************
   * DATA PROCESS *
   ************** */

  var _useState = useState(getParsedDates(defaultSelected, dateFormat)),
    _useState2 = _slicedToArray(_useState, 2),
    uncontrolledDate = _useState2[0],
    setUncontrolledDate = _useState2[1];
  var controlledDate = useMemo(function () {
    return getParsedDates(selected, dateFormat);
  }, [dateFormat, selected]);
  var parsedSelectedDate = typeof selected !== 'undefined' ? controlledDate : uncontrolledDate;
  var parsedMinDate = useMemo(function () {
    var date = getParsedDates(minDate, dateFormat);
    if (isValid(date)) {
      return date;
    }
    // Given min date is invalid, default to 5 years before
    if (isValid(parsedSelectedDate)) {
      date = parsedSelectedDate;
    } else {
      date = new Date();
    }
    return addYears(date, -5);
  }, [dateFormat, minDate, parsedSelectedDate]);
  var parsedMaxDate = useMemo(function () {
    var date = getParsedDates(maxDate, dateFormat);
    if (isValid(date)) {
      return date;
    }
    // Given max date is invalid, default to 5 years after
    if (isValid(parsedSelectedDate)) {
      date = parsedSelectedDate;
    } else {
      date = new Date();
    }
    return addYears(date, 5);
  }, [dateFormat, maxDate, parsedSelectedDate]);
  var parsedDisabledDates = useMemo(function () {
    return typeof disabledDates === 'function' ? disabledDates : getParsedDates(disabledDates, dateFormat);
  }, [dateFormat, disabledDates]);

  // if selected date is a disabled date, set initial focus on the first available date
  var initialFocusDate = useMemo(function () {
    return getFirstAvailableDate(parsedSelectedDate, parsedDisabledDates, parsedMinDate, parsedMaxDate);
  }, [parsedDisabledDates, parsedMaxDate, parsedMinDate, parsedSelectedDate]);
  var ariaLabelledby = useRef(identifier());
  var _useState3 = useState(initialFocusDate),
    _useState4 = _slicedToArray(_useState3, 2),
    focusDate = _useState4[0],
    setFocusDate = _useState4[1];
  var _useState5 = useState(false),
    _useState6 = _slicedToArray(_useState5, 2),
    dayFocusable = _useState6[0],
    setDayFocusable = _useState6[1];
  var month = useMemo(function () {
    return getMonth(focusDate);
  }, [focusDate]);
  var minYear = useMemo(function () {
    return getYear(parsedMinDate);
  }, [parsedMinDate]);
  var maxYear = useMemo(function () {
    return getYear(parsedMaxDate);
  }, [parsedMaxDate]);
  var year = useMemo(function () {
    return getValidYear(focusDate, minYear, maxYear);
  }, [focusDate, maxYear, minYear]);
  var monthMatrix = useMemo(function () {
    return getMonthMatrix(month, year);
  }, [month, year]);
  var yearRange = useMemo(function () {
    return getArrayFromRange(minYear, maxYear, 1);
  }, [maxYear, minYear]);
  var isNextMonthDisabled = useMemo(function () {
    return year === maxYear && month >= getMonth(parsedMaxDate);
  }, [year, month, maxYear, parsedMaxDate]);
  var isPrevMonthDisabled = useMemo(function () {
    return year === minYear && month <= getMonth(parsedMinDate);
  }, [year, month, minYear, parsedMinDate]);

  /** ********
   * ACTIONS *
   ********* */

  // handle focus ring on keyboard events
  useEffect(function () {
    if (forwardedRef && forwardedRef.current && dayFocusable) {
      forwardedRef.current.focus();
    }
  }, [dayFocusable, focusDate, forwardedRef]);
  var updateFocusDate = useCallback(function (newDate, isDayFocusable, isPageKeyCode) {
    if (isDayFocusable === void 0) {
      isDayFocusable = true;
    }
    if (isPageKeyCode === void 0) {
      isPageKeyCode = false;
    }
    // keyboard day events and new date is out of range, ignore
    if (isDayFocusable && !(isPageKeyCode || isDateInBetween(newDate, parsedMinDate, parsedMaxDate))) {
      return;
    }
    var date = newDate;
    // mouse events or keyboard page events and new date is disabled
    // find the first available day
    if (!isDayFocusable || isPageKeyCode) {
      date = getFirstAvailableDate(date, parsedDisabledDates, parsedMinDate, parsedMaxDate);
    }
    setFocusDate(date);
    setDayFocusable(isDayFocusable);
  }, [parsedDisabledDates, parsedMaxDate, parsedMinDate]);
  var updateSelectedDate = useCallback(function (date, meta) {
    setUncontrolledDate(date);
    if (onChange) {
      onChange({
        date: date,
        formatted: format(date, dateFormat)
      }, meta);
    }
  }, [dateFormat, onChange]);
  useIsomorphicLayoutEffect(function () {
    updateFocusDate(initialFocusDate, false);
  }, [initialFocusDate, updateFocusDate]);

  /** *********
   * BEHAVIOR *
   ********** */

  var onPrevMonthCallback = useCallback(function () {
    var newDate = addMonths(focusDate, -1);
    updateFocusDate(newDate, false);
  }, [focusDate, updateFocusDate]);
  var onNextMonthCallback = useCallback(function () {
    var newDate = addMonths(focusDate, 1);
    updateFocusDate(newDate, false);
  }, [focusDate, updateFocusDate]);
  var onYearChangeCallback = useCallback(function (e) {
    var newDate = setYear(focusDate, Number(e.target.value));
    updateFocusDate(newDate, false);
  }, [focusDate, updateFocusDate]);
  var onDayClickCallback = useCallback(function (e) {
    var newDate = new Date(year, month, Number(e.target.innerText));
    updateSelectedDate(newDate, {
      reason: DATE_PICKER_CHANGE_REASON.DROPDOWN_CLICK
    });
  }, [month, year, updateSelectedDate]);
  var onDayFocusCallback = useCallback(function (e) {
    var newDate = new Date(year, month, Number(e.target.innerText));
    updateFocusDate(newDate);
  }, [month, year, updateFocusDate]);
  var onDayKeyDownCallback = useCallback(function (e) {
    var newDate;
    switch (e.keyCode) {
      case KEY_CODES.ENTER:
      case KEY_CODES.SPACE:
        newDate = focusDate;
        updateSelectedDate(newDate, {
          reason: DATE_PICKER_CHANGE_REASON.DROPDOWN_KEYBOARD
        });
        break;
      case KEY_CODES.ARROW_RIGHT:
        newDate = getNextAvailableDay(focusDate, parsedDisabledDates, parsedMinDate, parsedMaxDate);
        if (newDate) {
          updateFocusDate(newDate);
        }
        break;
      case KEY_CODES.ARROW_LEFT:
        newDate = getPreviousAvailableDay(focusDate, parsedDisabledDates, parsedMinDate, parsedMaxDate);
        updateFocusDate(newDate);
        break;
      case KEY_CODES.ARROW_DOWN:
        newDate = addDays(focusDate, 7);
        updateFocusDate(newDate);
        break;
      case KEY_CODES.ARROW_UP:
        newDate = addDays(focusDate, -7);
        updateFocusDate(newDate);
        break;
      case KEY_CODES.PAGE_UP:
        newDate = addMonths(focusDate, -1);
        updateFocusDate(newDate, true, true);
        break;
      case KEY_CODES.PAGE_DOWN:
        newDate = addMonths(focusDate, 1);
        updateFocusDate(newDate, true, true);
        break;
      case KEY_CODES.HOME:
        newDate = addDays(focusDate, -getDay(focusDate));
        updateFocusDate(newDate);
        break;
      case KEY_CODES.END:
        newDate = addDays(focusDate, 6 - getDay(focusDate));
        updateFocusDate(newDate);
        break;
      default:
        break;
    }
    if (newDate) {
      e.stopPropagation();
      e.preventDefault();
    }
  }, [focusDate, updateFocusDate, updateSelectedDate, parsedDisabledDates, parsedMinDate, parsedMaxDate]);

  /** *******
   * RENDER *
   ******** */

  var renderDatePickerHeader = /*#__PURE__*/React.createElement(Flex, {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center"
  }, /*#__PURE__*/React.createElement(DatePickerButton, {
    "aria-label": "previous month",
    disabled: isPrevMonthDisabled,
    onClick: onPrevMonthCallback
  }, /*#__PURE__*/React.createElement(IconChevronLeft, null)), /*#__PURE__*/React.createElement(Flex, {
    display: "flex",
    justifyContent: "center",
    alignItems: "center"
  }, /*#__PURE__*/React.createElement(Heading, {
    id: ariaLabelledby.current,
    "aria-live": "polite",
    level: 6
  }, DATE_PICKER_MONTHS[month], /*#__PURE__*/React.createElement(VisuallyHidden, null, year)), /*#__PURE__*/React.createElement(DatePickerSelect, {
    value: year,
    onChange: onYearChangeCallback
  }, yearRange.map(function (value, index) {
    return (
      /*#__PURE__*/
      // eslint-disable-next-line zillow/react/no-array-index-key
      React.createElement("option", {
        key: index
      }, value)
    );
  }))), /*#__PURE__*/React.createElement(DatePickerButton, {
    "aria-label": "next month",
    disabled: isNextMonthDisabled,
    onClick: onNextMonthCallback
  }, /*#__PURE__*/React.createElement(IconChevronRight, null)));
  var renderCalendarHeader = /*#__PURE__*/React.createElement("tr", null, DATE_PICKER_DAYS.map(function (day, index) {
    return (
      /*#__PURE__*/
      // eslint-disable-next-line zillow/react/no-array-index-key
      React.createElement("th", {
        key: index,
        scope: "col",
        abbr: DATE_PICKER_DAYS[index]
      }, /*#__PURE__*/React.createElement(Text, {
        fontType: "bodySmallHeading"
      }, day.slice(0, 1)))
    );
  }));
  var renderCalendarBody = monthMatrix.map(function (week, weekIndex) {
    return (
      /*#__PURE__*/
      // eslint-disable-next-line zillow/react/no-array-index-key
      React.createElement("tr", {
        key: weekIndex
      }, week.map(function (day, dayIndex) {
        if (day === null) {
          // eslint-disable-next-line zillow/react/no-array-index-key
          return /*#__PURE__*/React.createElement("td", {
            key: weekIndex + "_" + dayIndex
          });
        }
        var currentDate = new Date(year, month, day);
        var today = isSameDay(currentDate, new Date());
        var pressed = isSameDay(currentDate, parsedSelectedDate);
        var focus = isSameDay(currentDate, focusDate);
        var disabled = isDisabledDate(currentDate, parsedDisabledDates, parsedMinDate, parsedMaxDate);
        return (
          /*#__PURE__*/
          // eslint-disable-next-line zillow/react/no-array-index-key
          React.createElement("td", {
            key: weekIndex + "_" + dayIndex
          }, /*#__PURE__*/React.createElement(DatePickerButton, {
            disabled: disabled,
            onKeyDown: onDayKeyDownCallback,
            onClick: onDayClickCallback,
            onFocus: onDayFocusCallback,
            ref: focus ? forwardedRef : undefined,
            pressed: pressed,
            tabIndex: focus ? '0' : '-1',
            today: today
          }, day))
        );
      }))
    );
  });
  return /*#__PURE__*/React.createElement(StyledDatePicker, _extends({
    "aria-labelledby": ariaLabelledby.current
  }, rest), renderDatePickerHeader, /*#__PURE__*/React.createElement("table", {
    role: "grid",
    "aria-labelledby": ariaLabelledby.current
  }, /*#__PURE__*/React.createElement("thead", null, renderCalendarHeader), /*#__PURE__*/React.createElement("tbody", null, renderCalendarBody)));
});
DatePicker.displayName = 'DatePicker';
DatePicker.propTypes = process.env.NODE_ENV !== "production" ? {
  /**
   * The format of the date when parsing date strings. This format will also be used for
   * formatting dates returned to the `onChange` callback.
   *
   * Formatting options can be seen in [date-fns](https://date-fns.org/v2.13.0/docs/format).
   */
  dateFormat: PropTypes.string,
  /**
   * An array of dates that are unavailable to be selected.
   */
  disabledDates: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.instanceOf(Date), PropTypes.string])), PropTypes.func]),
  /**
   * The oldest date allowed by the date picker. Defaults to 5 years before the selected date.
   */
  minDate: PropTypes.oneOfType([PropTypes.instanceOf(Date), PropTypes.string]),
  /**
   * The newest date allowed by the date picker. Defaults to 5 years after the selected date.
   */
  maxDate: PropTypes.oneOfType([PropTypes.instanceOf(Date), PropTypes.string]),
  /**
   * Callback function when a day is selected from the calendar using a mouse or keyboard.
   * The callback will receive two objects. One with the date object `date`, and the formatted
   * date string `formatted` as properties. Second, with the meta information.
   */
  onChange: PropTypes.func,
  /**
   * The selected date when using this as a
   * [controlled component](https://reactjs.org/docs/forms.html#controlled-components).
   */
  selected: PropTypes.oneOfType([PropTypes.instanceOf(Date), PropTypes.string]),
  /**
   * The default selected date when using this as an
   * [uncontrolled component](https://reactjs.org/docs/uncontrolled-components.html).
   */
  defaultSelected: PropTypes.oneOfType([PropTypes.instanceOf(Date), PropTypes.string])
} : {};
DatePicker.defaultProps = {
  dateFormat: 'MM/dd/yyyy',
  disabledDates: [],
  minDate: undefined,
  maxDate: undefined,
  onChange: undefined,
  selected: undefined,
  defaultSelected: new Date()
};